import React, { useState } from 'react';
import { FaTimes, FaEnvelope, FaCheckCircle, FaLock } from 'react-icons/fa';
import { sanitizeEmail } from '../../utils/inputSanitization';
import ValidatedInput from './ValidatedInput';
import PasswordInput from './PasswordInput';
import LoadingSpinner from './LoadingSpinner';
import { API_BASE_URL } from '../../config/runtimeConfig';
import './ForgotPasswordModal.scss';

// Email validation function
const validateEmail = (value) => {
  if (!value) return 'Email is required';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return 'Please enter a valid email address';
  }
  return null;
};

// Mirrors the server password rules in server/routes/authRoutes.js
// (min 8 chars, uppercase, lowercase, number, special character)
const validateNewPassword = (value) => {
  if (!value) return 'Password is required';
  if (value.length < 8) return 'Password must be at least 8 characters';
  if (!/[a-z]/.test(value)) return 'Password must include a lowercase letter';
  if (!/[A-Z]/.test(value)) return 'Password must include an uppercase letter';
  if (!/\d/.test(value)) return 'Password must include a number';
  if (!/[@$!%*?&]/.test(value)) return 'Password must include a special character (@ $ ! % * ? &)';
  return null;
};

const validateCode = (value) => {
  if (!value) return 'Code is required';
  if (!/^\d{6}$/.test(value)) return 'Enter the 6-digit code from your email';
  return null;
};

const parseApiError = (data, fallback) =>
  data?.message || data?.errors?.[0]?.msg || fallback;

/**
 * Reusable two-step password reset flow:
 * step 1 = enter email + send code, step 2 = code + new password, then success.
 * Used by ForgotPasswordModal (login page) and the standalone /reset-password view.
 */
export const PasswordResetFlow = ({
  initialEmail = '',
  onCodeSent,
  onDone,
  doneLabel = 'Back to Login',
}) => {
  const [step, setStep] = useState('email'); // 'email' | 'reset' | 'success'
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'

  const handleEmailChange = (e) => {
    setEmail(sanitizeEmail(e.target.value));
    setError('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();

    const emailValidation = validateEmail(email);
    if (emailValidation) {
      setError(emailValidation);
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/auth/otp/send-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(parseApiError(data, 'Failed to send reset code'));
      }

      if (onCodeSent) {
        await onCodeSent(email);
      }

      setStep('reset');
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendStatus === 'sending') return;

    try {
      setResendStatus('sending');
      setError('');

      const response = await fetch(`${API_BASE_URL}/auth/otp/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, purpose: 'password_reset' })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(parseApiError(data, 'Failed to resend code'));
      }

      setResendStatus('sent');
      setTimeout(() => setResendStatus('idle'), 4000);
    } catch (err) {
      console.error('Resend reset code error:', err);
      setResendStatus('error');
      setError(err.message || 'Failed to resend code. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    const codeValidation = validateCode(code);
    if (codeValidation) {
      setError(codeValidation);
      return;
    }

    const passwordValidation = validateNewPassword(newPassword);
    if (passwordValidation) {
      setError(passwordValidation);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/auth/otp/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(parseApiError(data, 'Failed to reset password'));
      }

      setStep('success');
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'email') {
    return (
      <>
        <div className="modal-icon">
          <FaEnvelope />
        </div>
        <p className="modal-description">
          Enter your email address and we'll send you a one-time code to reset your password.
        </p>

        <form onSubmit={handleSendCode} className="forgot-password-form">
          <div className="form-group">
            <label htmlFor="reset-email">Email Address</label>
            <ValidatedInput
              id="reset-email"
              type="email"
              name="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              validation={validateEmail}
              disabled={isSubmitting}
              required
            />
            {error && (
              <div className="error-message">{error}</div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !email}
            className="reset-password-submit-button"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" color="white" />
                Sending Reset Code...
              </>
            ) : (
              'Send Reset Code'
            )}
          </button>
        </form>
      </>
    );
  }

  if (step === 'reset') {
    return (
      <>
        <div className="modal-icon">
          <FaLock />
        </div>
        <p className="modal-description">
          We sent a 6-digit code to <strong>{email}</strong>.
          Enter it below along with your new password.
        </p>

        <form onSubmit={handleResetPassword} className="forgot-password-form">
          <div className="form-group">
            <label htmlFor="reset-code">Verification Code</label>
            <ValidatedInput
              id="reset-code"
              type="text"
              name="code"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              placeholder="6-digit code"
              validation={validateCode}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="reset-new-password">New Password</label>
            <PasswordInput
              id="reset-new-password"
              name="newPassword"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
              placeholder="New password"
              autoComplete="new-password"
              disabled={isSubmitting}
            />
            <p className="password-rules">
              At least 8 characters, with uppercase, lowercase, a number, and a special character.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="reset-confirm-password">Confirm New Password</label>
            <PasswordInput
              id="reset-confirm-password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              placeholder="Confirm new password"
              autoComplete="new-password"
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="reset-password-submit-button"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" color="white" />
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          <div className="resend-row">
            <span>Didn't get the code?</span>
            <button
              type="button"
              className="resend-link"
              onClick={handleResendCode}
              disabled={isSubmitting || resendStatus === 'sending'}
            >
              {resendStatus === 'sending' ? 'Sending...' : 'Resend Code'}
            </button>
            {resendStatus === 'sent' && (
              <span className="resend-feedback">New code sent!</span>
            )}
          </div>

          <button
            type="button"
            className="change-email-link"
            onClick={() => { setStep('email'); setCode(''); setError(''); setResendStatus('idle'); }}
            disabled={isSubmitting}
          >
            Use a different email
          </button>
        </form>
      </>
    );
  }

  return (
    <div className="success-state">
      <div className="success-icon">
        <FaCheckCircle />
      </div>
      <h3>Password Updated</h3>
      <p>
        Your password has been reset successfully.
      </p>
      <p className="success-note">
        You can now sign in with your new password.
      </p>
      <button
        type="button"
        className="back-to-login"
        onClick={onDone}
      >
        {doneLabel}
      </button>
    </div>
  );
};

const ForgotPasswordModal = ({ isOpen, onClose, onSubmit }) => {
  if (!isOpen) return null;

  return (
    <div className="forgot-password-overlay">
      <div className="forgot-password-modal">
        <div className="modal-header">
          <h2>Reset Password</h2>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        <div className="modal-content">
          <PasswordResetFlow onCodeSent={onSubmit} onDone={onClose} />
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
